import React, { useState, useContext, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  ScrollView,
  useWindowDimensions,
} from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { GlobalStateContext } from '../../../components/general/GlobalStateProvider';
import { useNavigate } from 'react-router-dom';
import { usePhoneAuth } from '../../../sms/composite/PhoneAuthManager';
import PhoneVerificationModal from '../../../sms/components/PhoneVerificationModal';
import DataOps from '../../../data/DataOps';
import EditFieldModal from '../components/EditFieldModal';
import HostCoachesSection from './components/HostCoachesSection';
import ProfileHeader from './components/ProfileHeader';
import ContactInfoSection from './components/ContactInfoSection';
import FamilyMembersSection from './components/FamilyMembersSection';
import AddressSection from './components/AddressSection';
import { AccountPageTitles, AccountDescriptions, MembersSectionTitles, MembersSectionSubtitles, AddButtonTexts, EmptyStateByAccount } from '../../../constants/AccountTexts';
import ProfessionalExperienceSection from './components/ProfessionalExperienceSection';
import CertificationsSection from './components/CertificationsSection';
import { buildCoachUpdateFromProfileData } from '../../../data/Interface/Coach';

interface PersonalInfoProps {
  isEditing?: boolean;
}

interface PersonalInfoPageProps extends PersonalInfoProps {
  isEditing?: boolean;
  setIsEditing?: (editing: boolean) => void;
}

const PersonalInfoPage: React.FC<PersonalInfoPageProps> = ({ isEditing: propIsEditing }) => {
  // Always in edit mode for ease of use, individual fields save on change
  const isEditing = true;
  const { padding, user, setUser, userCollection, activeChild, setActiveChild } = useContext(GlobalStateContext);
  const navigate = useNavigate();
  const { width: windowWidth } = useWindowDimensions();
  const isWideLayout = windowWidth >= 960;
  const [profileData, setProfileData] = useState<{ [key: string]: any }>({
    profileName: 'My profile'
  });
  const [coaches, setCoaches] = useState<string[]>([]);
  const [editingField, setEditingField] = useState<any>(null);
  const [hostMainCamp, setHostMainCamp] = useState<any | null>(null);
  const [pendingPhoneValue, setPendingPhoneValue] = useState<string>('');

  const [showEditFieldModal, setShowEditFieldModal] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Phone verification using PhoneAuthManager hook
  const phoneAuth = usePhoneAuth({
    firebaseApp: DataOps.app,
    user,
    onUserUpdate: setUser,
    onProfileDataUpdate: (phone) => {
      setProfileData(prev => ({ ...prev, phone }));
    },
    recaptchaContainerId: 'recaptcha-container-phone',
  });

  // Dynamic text based on account type
  // Normalize accountType: mobile stores 'user', web used to store 'family'; treat both as 'user'
  const resolvedType = (user?.accountType === 'family' || user?.accountType === 'user')
    ? 'user'
    : (user?.accountType || 'user');

  const getMembersSectionTitle = () => MembersSectionTitles[resolvedType] ?? MembersSectionTitles['user'];

  const getMembersSubtitle = () => MembersSectionSubtitles[resolvedType] ?? MembersSectionSubtitles['user'];

  const getEmptyStateText = () => EmptyStateByAccount[resolvedType] ?? EmptyStateByAccount['user'];

  const getAccountDescription = () => AccountDescriptions[resolvedType] ?? AccountDescriptions['user'];

  const getPageTitle = () => AccountPageTitles[resolvedType] ?? AccountPageTitles['user'];

  useEffect(() => {
    if (user) {
      setProfileData({
        profileName: user.userName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'My Profile',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || '',
        street: user.address?.street || '',
        city: user.address?.city || '',
        state: user.address?.state || '',
        zipCode: user.address?.zipCode || '',
        country: user.address?.country || '',
        bio: (user as any).bio || '',
        yearsOfExperience: (user as any).yearsOfExperience ?? (user as any).experienceYears ?? '',
        specialties: Array.isArray((user as any).specialties) ? (user as any).specialties : ((user as any).specialties || ''),
        certifications: Array.isArray((user as any).certifications) ? (user as any).certifications : ((user as any).certifications || ''),
        languages: Array.isArray((user as any).languages) ? (user as any).languages : ((user as any).languages || ''),
      });

      // Load coaches if user is a host and fetch primary camp to keep in sync with camp screen
      if (user.accountType === 'host') {
        setCoaches((user as any).coaches || []);
        const primaryCampId = (user as any).camps && (user as any).camps[0];
        if (primaryCampId) {
          (async () => {
            const campData = await DataOps.fetchDataFromFirebase('camp', primaryCampId);
            if (campData) setHostMainCamp({ ...campData, campId: primaryCampId });
          })();
        } else {
          setHostMainCamp(null);
        }
      }
    }
  }, [user]);

  // handleChange removed as we now save immediately on field edit

  const saveProfileToFirebase = useCallback(async (data: { [key: string]: any }) => {
    if (!user) return;
    setSaveError(null);
    setIsSaving(true);
    const emailChanged = data.email !== user.email;
    const phoneChanged = data.phone !== user.phone;

    const updatedUser: any = {
      ...user,
      firstName: data.firstName !== undefined ? data.firstName : user.firstName,
      lastName: data.lastName !== undefined ? data.lastName : user.lastName,
      userName: data.profileName !== undefined ? data.profileName : user.userName,
      email: data.email !== undefined ? data.email : user.email,
      phone: data.phone !== undefined ? data.phone : user.phone,
      bio: data.bio !== undefined ? data.bio : (user as any).bio,
      yearsOfExperience: data.yearsOfExperience !== undefined ? data.yearsOfExperience : (user as any).yearsOfExperience,
      specialties: data.specialties !== undefined ? data.specialties : (user as any).specialties,
      certifications: data.certifications !== undefined ? data.certifications : (user as any).certifications,
      languages: data.languages !== undefined ? data.languages : (user as any).languages,
      address: {
        street: data.street !== undefined ? data.street : user.address?.street,
        city: data.city !== undefined ? data.city : user.address?.city,
        state: data.state !== undefined ? data.state : user.address?.state,
        zipCode: data.zipCode !== undefined ? data.zipCode : user.address?.zipCode,
        country: data.country !== undefined ? data.country : user.address?.country,
      },
      emailVerified: emailChanged ? false : user.emailVerified,
      phoneVerified: phoneChanged ? false : user.phoneVerified,
      accountType: user.accountType || 'user',
      children: user.children || [],
    };

    // Remove undefined values to avoid Firestore issues
    const cleanedUser = Object.fromEntries(
      Object.entries(updatedUser).filter(([_, value]) => value !== undefined)
    ) as any;

    try {
      if (user.accountType === 'coach') {
        const coachUpdate = buildCoachUpdateFromProfileData(data);
        Object.assign(cleanedUser, coachUpdate);
      }

      const docId = user.userId || user.id || (user as any).userId;
      const collectionName = DataOps.getCollectionNameForAccountType(user.accountType);

      console.log('[PersonalInfoPage] DEBUG: Starting save process', {
        collectionName,
        docId,
        dataKeys: Object.keys(cleanedUser)
      });

      const success = await DataOps.saveCustomDataToFirebase(collectionName, docId, cleanedUser, true);
      if (!success) {
        throw new Error('Failed to save profile data');
      }

      console.log('[PersonalInfoPage] DEBUG: Save result from DataOps:', success);

      // Update local storage and context to keep everything in sync
      await DataOps.saveDataToStorage('user', cleanedUser);
      setUser(cleanedUser);
    } catch (error) {
      console.error('Error saving user profile:', error);
      setSaveError('Could not save changes. Please try again.');
      alert('Failed to save profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [user, setUser]);

  const handleSaveWithoutToggle = useCallback(async () => {
    await saveProfileToFirebase(profileData);
  }, [profileData, saveProfileToFirebase]);

  // handleSaveWithoutToggle removed as we save per field

  const handleEditField = (field: string) => {
    const fieldConfig: Record<string, {
      fieldKey: string;
      label: string;
      value: string;
      placeholder: string;
      keyboardType: 'default' | 'email-address' | 'phone-pad' | 'numeric' | 'number-pad';
      multiline?: boolean;
      maxLength?: number;
    }> = {
      profileName: {
        fieldKey: 'profileName',
        label: 'Profile Name',
        value: profileData.profileName || '',
        placeholder: 'Enter your preferred name',
        keyboardType: 'default',
      },
      firstName: {
        fieldKey: 'firstName',
        label: 'First Name',
        value: profileData.firstName || '',
        placeholder: 'Enter your first name',
        keyboardType: 'default',
      },
      lastName: {
        fieldKey: 'lastName',
        label: 'Last Name',
        value: profileData.lastName || '',
        placeholder: 'Enter your last name',
        keyboardType: 'default',
      },
      email: {
        fieldKey: 'email',
        label: 'Email Address',
        value: profileData.email || '',
        placeholder: 'Enter your email address',
        keyboardType: 'email-address',
      },
      phone: {
        fieldKey: 'phone',
        label: 'Phone Number',
        value: profileData.phone || '',
        placeholder: 'Enter your phone number',
        keyboardType: 'phone-pad',
      },
      bio: {
        fieldKey: 'bio',
        label: 'Bio',
        value: profileData.bio || '',
        placeholder: 'Tell families about your coaching background',
        keyboardType: 'default',
        multiline: true,
        maxLength: 2000,
      },
      yearsOfExperience: {
        fieldKey: 'yearsOfExperience',
        label: 'Years of Experience',
        value: String(profileData.yearsOfExperience || ''),
        placeholder: 'e.g. 5',
        keyboardType: 'number-pad',
      },
      specialties: {
        fieldKey: 'specialties',
        label: 'Specialties',
        value: Array.isArray(profileData.specialties) ? profileData.specialties.join(', ') : (profileData.specialties || ''),
        placeholder: 'Comma-separated list, e.g. Soccer, Tennis',
        keyboardType: 'default',
      },
      certifications: {
        fieldKey: 'certifications',
        label: 'Certifications',
        value: Array.isArray(profileData.certifications) ? profileData.certifications.join(', ') : (profileData.certifications || ''),
        placeholder: 'Comma-separated list',
        keyboardType: 'default',
      },
      languages: {
        fieldKey: 'languages',
        label: 'Languages',
        value: Array.isArray(profileData.languages) ? profileData.languages.join(', ') : (profileData.languages || ''),
        placeholder: 'Comma-separated list, e.g. English, Spanish',
        keyboardType: 'default',
      },
    };

    const cfg = fieldConfig[field];
    if (!cfg) return;
    setEditingField(cfg);
    setShowEditFieldModal(true);
  };

  const handleSaveField = async (value: string) => {
    if (!editingField || !user) return;

    const fieldKey = editingField.fieldKey as string;
    if (!fieldKey) return;

    if (fieldKey === 'email' && value.trim() !== (user.email || '')) {
      const emailInUse = await DataOps.isEmailInUse(value.trim(), user.userId);
      if (emailInUse) {
        alert('This email is already in use by another account. Please use a different email.');
        return;
      }
    }

    if (fieldKey === 'phone' && value.replace(/\D/g, '') !== (user.phone || '').replace(/\D/g, '')) {
      const phoneInUse = await DataOps.isPhoneInUse(value, user.userId);
      if (phoneInUse) {
        alert('This phone number is already in use by another account. Please use a different phone number.');
        return;
      }
    }

    let processed: any = value;
    if (['specialties', 'certifications', 'languages'].includes(fieldKey)) {
      processed = String(value)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    } else if (fieldKey === 'yearsOfExperience') {
      processed = value.replace(/[^0-9]/g, '');
    } else if (fieldKey === 'email') {
      processed = value.trim();
    }

    const merged = { ...profileData, [fieldKey]: processed };
    setProfileData(merged);

    if (fieldKey === 'phone' && value.replace(/\D/g, '') !== (user.phone || '').replace(/\D/g, '')) {
      setPendingPhoneValue(value);
      setShowEditFieldModal(false);
      phoneAuth.handlePhoneVerification(value);
      return;
    }

    await saveProfileToFirebase(merged);
  };

  const handleAddressChange = async (address: { street: string; city: string; state: string; zipCode: string; country?: string }) => {
    const merged = {
      ...profileData,
      street: address.street,
      city: address.city,
      state: address.state,
      zipCode: address.zipCode,
      country: address.country || '',
    };
    setProfileData(merged);
    await saveProfileToFirebase(merged);
  };

  return (
    <ScrollView
      contentContainerStyle={[pageStyles.container, { paddingHorizontal: padding }]}
      showsVerticalScrollIndicator={false}
    >
      <View style={pageStyles.hero}>
        <View style={pageStyles.topNav}>
          <Pressable
            style={({ pressed }) => [
              pageStyles.backButton,
              pressed && { opacity: 0.7 }
            ]}
            onPress={() => navigate('/account')}
          >
            <View style={pageStyles.backIconCircle}>
              <MaterialCommunityIcons name="arrow-left" size={20} color="#1E293B" />
            </View>
            <Text style={pageStyles.backButtonText}>Settings</Text>
          </Pressable>

          <View style={pageStyles.statusIndicator}>
            <View style={[pageStyles.dot, { backgroundColor: isSaving ? '#F59E0B' : '#3C8248' }]} />
            <Text style={pageStyles.statusText}>
              {isSaving ? 'Syncing...' : 'All changes saved'}
            </Text>
          </View>
        </View>

        <View style={pageStyles.headerContent}>
          <Text style={pageStyles.pageTitle}>{getPageTitle()}</Text>
          <Text style={pageStyles.pageSubtitle}>
            Update your account details and profile information to keep your experience personalized.
          </Text>
        </View>

        {!!saveError && (
          <View style={pageStyles.errorBanner}>
            <MaterialCommunityIcons name="alert-circle" size={20} color="#EF4444" />
            <Text style={pageStyles.errorBannerText}>{saveError}</Text>
          </View>
        )}
      </View>

      <View style={pageStyles.singleColumnContainer}>
        <ProfileHeader
          user={user}
          isEditing={isEditing}
          profileName={profileData.profileName || ''}
          onEditName={() => handleEditField('profileName')}
          getAccountDescription={getAccountDescription}
          setUser={setUser}
        />

        <ContactInfoSection
          isEditing={isEditing}
          profileData={profileData}
          onEditField={handleEditField}
          user={user}
        />

        <AddressSection
          isEditing={isEditing}
          profileData={profileData}
          onAddressChange={handleAddressChange}
        />

        {user?.accountType === 'coach' && (
          <>
            <ProfessionalExperienceSection
              isEditing={isEditing}
              profileData={profileData}
              onEditField={handleEditField}
            />
            <CertificationsSection
              isEditing={isEditing}
              profileData={profileData}
              onEditField={handleEditField}
            />
          </>
        )}

        {user?.accountType !== 'coach' && (
          <>
            {user?.accountType === 'host' ? (
              <HostCoachesSection
                isEditing={isEditing}
                user={user}
                coaches={coaches}
                hostMainCamp={hostMainCamp}
                setHostMainCamp={setHostMainCamp}
                getMembersSectionTitle={getMembersSectionTitle}
                getMembersSubtitle={getMembersSubtitle}
                getEmptyStateText={getEmptyStateText}
              />
            ) : (
              <FamilyMembersSection
                isEditing={isEditing}
                user={user}
                activeChild={activeChild}
                setActiveChild={setActiveChild}
                getMembersSectionTitle={getMembersSectionTitle}
                getMembersSubtitle={getMembersSubtitle}
                getEmptyStateText={getEmptyStateText}
              />
            )}
          </>
        )}
      </View>

      <View style={{ height: 120 }} />

      {showEditFieldModal && editingField && (
        <EditFieldModal
          visible={showEditFieldModal}
          onClose={() => setShowEditFieldModal(false)}
          onSave={handleSaveField}
          field={{
            label: editingField.label,
            value: editingField.value,
            placeholder: editingField.placeholder,
            keyboardType: editingField.keyboardType,
            multiline: editingField.multiline,
            maxLength: editingField.maxLength,
          }}
          saveButtonText={editingField?.label === 'Phone Number' ? 'Send' : 'Save'}
        />
      )}

      <PhoneVerificationModal
        visible={phoneAuth.showPhoneVerification}
        onClose={() => {
          phoneAuth.handleCloseVerification();
          if (pendingPhoneValue) {
            setEditingField({
              label: 'Phone Number',
              value: pendingPhoneValue,
              placeholder: 'Enter your phone number',
              keyboardType: 'phone-pad' as const,
            });
            setShowEditFieldModal(true);
            setPendingPhoneValue('');
          }
        }}
        onVerify={phoneAuth.handleVerifyPhone}
        onResend={phoneAuth.handleResendPhoneCode}
        isLoading={phoneAuth.isPhoneVerifying}
        verificationCode={phoneAuth.verificationCode}
        setVerificationCode={phoneAuth.setVerificationCode}
        phoneE164={phoneAuth.pendingPhoneE164}
        canVerify={!!phoneAuth.confirmationResultRef}
      />

      {/* reCAPTCHA container */}
      <div id="recaptcha-container-phone" style={{ height: 0, width: 0, overflow: 'hidden' }}></div>

    </ScrollView>
  );
};

export default PersonalInfoPage;

const pageStyles = StyleSheet.create({
  container: {
    paddingTop: 32,
    backgroundColor: '#FFFFFF',
    flexGrow: 1,
  },
  hero: {
    marginBottom: 40,
  },
  singleColumnContainer: {
    width: '100%',
    paddingHorizontal: 0,
    gap: 32,
  },
  topNav: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 40,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  backIconCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 5,
    elevation: 2,
  },
  backButtonText: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1E293B',
  },
  statusIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#E2E8F0',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    gap: 8,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#64748B',
  },
  headerContent: {
    alignItems: 'center',
    paddingHorizontal: 16,
  },
  pageTitle: {
    fontSize: 36,
    fontWeight: '900',
    color: '#1E293B',
    textAlign: 'center',
    letterSpacing: -1,
    marginBottom: 12,
  },
  pageSubtitle: {
    fontSize: 16,
    color: '#64748B',
    textAlign: 'center',
    lineHeight: 24,
    maxWidth: 500,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: '#FEF2F2',
    borderWidth: 1,
    borderColor: '#FEE2E2',
    borderRadius: 16,
    paddingHorizontal: 16,
    paddingVertical: 14,
    marginTop: 24,
    marginHorizontal: 16,
  },
  errorBannerText: {
    flex: 1,
    color: '#B91C1C',
    fontSize: 14,
    fontWeight: '600',
  },
});


